<script setup lang="ts">
import type { FunctionalComponent, SVGAttributes } from "vue";
import { useDeleteAction } from "~/core/composables/useDeleteAction";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    title: string;
    buttonClass?: string | string[];
    icon?: FunctionalComponent<SVGAttributes>;
    iconClass?: string | string[];
    /** icon size in px */
    iconSize?: number;
    confirmTitle: string;
    confirmDescription: string;
    okLabel?: string;
    cancelLabel?: string;
    deleteFn: () => Promise<void>;
  }>(),
  {
    disabled: false,
    buttonClass: "invisible",
    okLabel: undefined,
    cancelLabel: undefined,
    icon: ILxcLightTrash2,
    iconClass: undefined,
    iconSize: 20,
  },
);

const emits = defineEmits(["success"]);

const okLabel = computed(() => props.okLabel || t("button.confirm"));
const cancelLabel = computed(() => props.cancelLabel || t("button.cancel"));
const iconSizeComputed = computed(() => props.iconSize + "px");

const {
  deleteConfirmDialogVisible,
  toggleDeleteConfirmVisible,
  onDeleteConfirm,
  onDeleteClick,
  isLoading,
} = useDeleteAction({
  deleteFn: props.deleteFn,
  onSuccess: () => emits("success"),
});
</script>

<template>
  <lxc-button
    html-type="button"
    type="borderless"
    :disabled="disabled || isLoading"
    :icon="!isLoading ? icon : undefined"
    :icon-class="iconClass"
    :title="title"
    :class="buttonClass"
    :is-loading="isLoading"
    @click.prevent.stop="onDeleteClick"
  />

  <confirm-modal
    :is-dialog-visible="deleteConfirmDialogVisible"
    :title="confirmTitle"
    :description="confirmDescription"
    :ok-label="okLabel"
    :cancel-label="cancelLabel"
    :icon="ILxcLightTrash2"
    icon-color-theme="error"
    color-theme="danger"
    :is-loading="isLoading"
    @confirm="onDeleteConfirm"
    @cancel="toggleDeleteConfirmVisible"
  />
</template>

<style scoped lang="scss">
button {
  :deep(svg) {
    height: v-bind(iconSizeComputed);
    width: v-bind(iconSizeComputed);
  }
}
</style>
